import { useState, useEffect } from 'react';

/**
 * Hook to extract texture token for client-side apps
 * Supports methods that work without SSR:
 * 1. URL query parameter ('query')
 * 2. Global window variable ('global') - for script injection
 * 3. PostMessage from parent window ('post') - for iframe embedding
 * 4. HTTP-only cookie ('cookie') - secure server-side token storage
 *
 * @param {string} method - Required extraction method: 'query', 'global', 'post', or 'cookie'
 * @param {object} routerQuery - Router query object for 'query' method
 * @param {function} replaceUrl - Function to replace URL for 'query' method
 * @param {string} testCookieMode - Set a header that triggers the API to add a simulated cookie for testing
 */
export function useTextureTokenExtraction(method, routerQuery = {}, replaceUrl = null, testCookieMode = false) {
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [responseData, setResponseData] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let cleanup = null;

    const extractToken = async () => {
      // Fail immediately if no method specified
      if (!method) {
        if (isMounted) {
          setIsLoading(false);
        }
        return;
      }

      let extractedToken = null;

      // Method 1: Check for global window variable (script injection)
      if (method === 'global' && typeof window !== 'undefined' && window.__TEXTURE_TOKEN__) {
        extractedToken = window.__TEXTURE_TOKEN__;
        // Clear the global variable for security
        delete window.__TEXTURE_TOKEN__;
        if (isMounted) {
          setToken(extractedToken);
          setIsLoading(false);
        }
        return;
      }

      // Method 2: Check URL query parameters
      if (method === 'query') {
        const queryToken = routerQuery.token;
        if (queryToken) {
          extractedToken = queryToken;
          // Remove token, authType, and tokenVia from URL for security using the provided replaceUrl function
          if (replaceUrl) {
            const { token, authType, tokenVia, ...cleanQuery } = routerQuery;
            const queryString = new URLSearchParams(cleanQuery).toString();
            const newUrl = window.location.pathname + (queryString ? '?' + queryString : '');
            replaceUrl(newUrl);
          }
        }
        if (isMounted) {
          setToken(extractedToken);
          setIsLoading(false);
        }
        return;
      }

      // Method 3: Listen for postMessage (for iframe embedding)
      if (method === 'post') {
        const handleMessage = (event) => {
          // Only accept messages from trusted origins
          // TODO: Add origin validation based on your requirements
          if (event.data && event.data.type === 'TEXTURE_TOKEN') {
            if (isMounted) {
              setToken(event.data.token);
              setIsLoading(false);
            }
            // Remove listener after receiving token
            window.removeEventListener('message', handleMessage);
            if (cleanup) {
              cleanup();
            }
          }
        };

        window.addEventListener('message', handleMessage);

        // Send ready message to parent (if in iframe)
        if (window.parent !== window) {
          window.parent.postMessage({ type: 'TEXTURE_TOKEN_READY' }, '*');
        }

        // Set timeout to stop loading if no token received
        const timeout = setTimeout(() => {
          if (isMounted) {
            setIsLoading(false);
          }
          window.removeEventListener('message', handleMessage);
        }, 1000);

        cleanup = () => {
          clearTimeout(timeout);
          window.removeEventListener('message', handleMessage);
        };
        return;
      }

      // Method 4: Check HTTP-only cookie
      if (method === 'cookie') {
        // For HTTP-only cookies, we need to make a request to get the token
        // since client-side JavaScript cannot directly access HTTP-only cookies
        let response_data = null;
        try {
          // Use full API endpoint URL to ensure proper domain resolution
          // For testing with proxy server, you can override this
          const apiEndpoint = process.env.NEXT_PUBLIC_API_ENDPOINT || '';
          const useProxy = window.location.search.includes('useProxy=true');
          const endpoint = useProxy ? 'http://localhost:8080' : apiEndpoint;

          const headers = {
            'Content-Type': 'application/json',
          };
          if (testCookieMode) {
            headers['x-test-token-cookie'] = 'true';
          }
          const response = await fetch(`${endpoint}/texture-token`, {
            method: 'GET',
            credentials: 'include', // Include cookies in the request
            headers,
          });

          if (response.ok) {
            response_data = await response.json();

            // Handle the API response structure: { status: "success", data: { token, origin, ... } }
            if (response_data.status === 'success' && response_data.data) {
              extractedToken = response_data.data.token;

              // Log domain information for debugging
              if (response_data.data.origin) {
                console.debug('Texture token extracted from cookie, origin:', response_data.data.origin);
              }
              if (response_data.data.requestInfo) {
                console.debug('Cookie extraction request info:', response_data.data.requestInfo);
              }
            } else {
              console.warn('Unexpected API response structure:', response_data);
            }
          }
        } catch (error) {
          console.warn('Failed to extract token from cookie:', error);
        }

        if (isMounted) {
          setToken(extractedToken);
          setResponseData(response_data);
          setIsLoading(false);
        }
        return;
      }

      // Invalid method specified
      if (isMounted) {
        setIsLoading(false);
      }
    };

    extractToken();

    return () => {
      isMounted = false;
      if (cleanup) {
        cleanup();
      }
    };
  }, [method, routerQuery, replaceUrl, testCookieMode]);

  return { token, isLoading, responseData };
}
